import React, { createContext, useContext, useState, useRef, useCallback, ReactNode } from 'react';
import { createMapping, updateMapping } from '../api/client';

export interface WizardState {
  // Step 1: Project Info
  mappingName: string;
  mappingDescription: string;

  // Step 2: PDF Template
  pdfTemplateId: string | null;

  // Step 3: Data Sources
  dataSources: Array<{
    id: string;
    type: 'master' | 'detail';
    role: 'primary' | 'secondary';
  }>;

  // Step 4: Relationships
  relationships: Array<{
    master_source_id: string;
    detail_source_id: string;
    master_field: string;
    detail_field: string;
  }>;

  // Step 5: Field Mappings (source_id -> data_field -> pdf_field)
  fieldMappings: Record<string, Record<string, string>>;

  // Filename pattern for generated PDFs
  filenamePattern: string;

  // Draft support
  mappingId: string | null; // ID if editing existing draft
  isDraft: boolean;
  currentStep: number;
}

interface WizardContextType {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
  resetState: () => void;
  saveDraft: (currentStep: number, updates?: Partial<WizardState>) => Promise<void>;
}

const initialState: WizardState = {
  mappingName: '',
  mappingDescription: '',
  pdfTemplateId: null,
  dataSources: [],
  relationships: [],
  fieldMappings: {},
  filenamePattern: '',
  mappingId: null,
  isDraft: false,
  currentStep: 1,
};

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const WizardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<WizardState>(initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const updateState = useCallback((updates: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  const saveDraft = useCallback(async (currentStep: number, updates?: Partial<WizardState>) => {
    const current = stateRef.current;
    const merged = updates ? { ...current, ...updates } : current;

    const draftData = {
      name: merged.mappingName || 'Unbenannter Entwurf',
      description: merged.mappingDescription,
      pdf_template_id: merged.pdfTemplateId || '',
      data_sources: merged.dataSources,
      relationships: merged.relationships,
      field_mappings: merged.fieldMappings,
      filename_pattern: merged.filenamePattern,
      is_draft: true,
      current_step: currentStep,
    };

    const mappingId = updates?.mappingId || merged.mappingId;

    if (mappingId) {
      await updateMapping(mappingId, draftData);
      setState((prev) => ({ ...prev, ...updates, currentStep }));
    } else {
      const mapping = await createMapping(draftData);
      setState((prev) => ({ ...prev, ...updates, mappingId: mapping.id, currentStep }));
    }
  }, []);

  return (
    <WizardContext.Provider value={{ state, updateState, resetState, saveDraft }}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = (): WizardContextType => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
};
